import {track} from "@amplitude/analytics-browser";
import {debounce, isEqual} from "lodash";
import type {NextPage} from "next";
import Head from "next/head";
import {useRouter} from "next/router";
import React, {useEffect, useMemo, useState} from "react";

import Search from "../components/Search";
import {APP_DESCRIPTION, APP_NAME} from "../constants";
import {
  queryStringToSearchState,
  searchStateToQueryString,
} from "../lib/search";
import type {SearchState} from "../lib/types";
import * as utils from "../lib/utils";

/**
 * Search page
 *
 * Displays a map with search results and allows user to filter them
 */
export default function SearchPage({}: NextPage & {whyDidYouRender?: boolean}) {
  const router = useRouter();
  const queryString = utils.parseQueryString(router.asPath);
  const [searchState, setSearchState] = useState<SearchState | undefined>(
    queryString ? queryStringToSearchState(queryString) : undefined
  );

  useEffect(() => {
    track("Search Page Viewed");
  }, []);

  const normalizeState = (state: SearchState): SearchState => {
    return {
      ...state,
      // Remove non-inputs
      results: [],
      showSearchLoader: false,
      runTutorial: false,
      hover: null,
      selection: null,
      mapRect: null,
      version: -1,
    };
  };

  const updateUrl = useMemo(() => {
    return debounce((state: SearchState) => {
      router.replace(`/search?${searchStateToQueryString(state)}`);
    }, 600);
  }, []);

  useEffect(() => {
    if (!searchState) {
      return;
    }

    updateUrl(searchState);
  }, [searchState]);

  return (
    <>
      <Head>
        <meta name="description" content={APP_DESCRIPTION} />

        <meta property="og:title" content={APP_NAME} />
        <meta property="og:description" content={APP_DESCRIPTION} />
      </Head>

      <main>
        <Search
          initialState={searchState}
          onStateChange={state => {
            const normalized = normalizeState(state);
            if (!isEqual(normalized, searchState)) {
              setSearchState(normalized);
            }
          }}
        />
      </main>
    </>
  );
}

// SearchPage.whyDidYouRender = true;
